import React, { useEffect } from 'react';
import { Controller, type Path, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Checkbox } from '@mui/material';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Typography from '@mui/material/Typography';
import { z } from 'zod';

import type { TemplateCleanupPayload } from '../../types';

// Zod schema for validation
const templateSectionSchema = z.object({
  module_id: z.string(),
  display_name: z.string(),
  should_remove: z.boolean(),
});

const templateCleanupSchema = z.object({
  template_id: z.string(),
  template_name: z.string(),
  sections: z.array(templateSectionSchema),
});

type FormData = z.infer<typeof templateCleanupSchema>;

interface TemplateCleanupFormProps {
  payload: TemplateCleanupPayload;
  onChange: (updatedPayload: TemplateCleanupPayload) => void;
  readOnly?: boolean;
}

export const TemplateCleanupForm: React.FC<TemplateCleanupFormProps> = ({
  payload,
  onChange,
  readOnly = false,
}) => {
  const { control, watch } = useForm<FormData>({
    resolver: zodResolver(templateCleanupSchema),
    defaultValues: payload,
  });

  // Watch for changes and call onChange
  useEffect(() => {
    const subscription = watch(value => {
      if (value.sections) {
        onChange(value as TemplateCleanupPayload);
      }
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [watch, onChange]);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Remove the following sections from <strong>{payload.template_name}</strong>:
      </Typography>
      <FormGroup>
        {payload.sections.map((section, index) => {
          const fieldName = `sections.${String(index)}.should_remove` as Path<FormData>;
          return (
            <Controller
              key={section.module_id}
              name={fieldName}
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={Boolean(field.value)}
                      disabled={readOnly}
                      size="small"
                    />
                  }
                  label={section.display_name}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.875rem',
                    },
                  }}
                />
              )}
            />
          );
        })}
      </FormGroup>
    </Box>
  );
};
